import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import {
	Box,
	DialogContent,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { updateInvoicePaymentRequest } from 'redux/reducers/invoiceManagement/reducer';
import { DatePickerComponent } from 'ui/DatePickerComponent';
import { TInvoicePaymentDialogProps } from './types';
import { PAID_THROUGH_OPTIONS } from '../constants';

export const InvoicePaymentDialog: FC<TInvoicePaymentDialogProps> = ({
	open,
	setClose,
	data,
	invoiceListRequestPayload,
}) => {
	const dispatch = useDispatch();
	const [paymentDate, setPaymentDate] = useState<DateTime | null>(DateTime.now());

	const reset = () => {
		setPaymentDate(DateTime.now());
	};

	const formik = useFormik({
		initialValues: {
			paidAmount: '',
			paidThrough: '',
		},
		enableReinitialize: true,
		onSubmit: (values) => {
			if (!data) return;

			const amount = data.total_invoice_amount_in_eur ?? data.invoiced_amount_in_eur ?? 0;
			const paidAmount = Number(values.paidAmount);

			dispatch(
				updateInvoicePaymentRequest({
					body: {
						invoice_id: data.invoice_id,
						payment_date: paymentDate
							? (paymentDate.toUTC().toISO({ suppressMilliseconds: false }) ?? '')
							: new Date().toISOString(),
						amount,
						paid_amount: paidAmount,
						paid_thruogh: values.paidThrough,
					},
					invoiceListRequest: invoiceListRequestPayload,
				}),
			);
			formik.resetForm();
			reset();
			setClose();
		},
	});

	useEffect(() => {
		if (!open) return;
		setPaymentDate(DateTime.now());
		const total = data?.total_invoice_amount_in_eur ?? data?.invoiced_amount_in_eur ?? 0;
		const alreadyPaid = data?.paid_amount ?? 0;
		const remainingAmount = Math.max(0, total - alreadyPaid);
		formik.setFieldValue('paidAmount', remainingAmount > 0 ? remainingAmount.toFixed(2) : '');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, data]);

	return (
		<Dialog
			open={open}
			sx={{
				'& .MuiDialog-paper': {
					width: '600px',
					maxWidth: '600px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary">Mark as paid or edit payment information</DialogTitle>
			<form onSubmit={formik.handleSubmit}>
				<DialogContent
					sx={{
						p: '0 12px 0px 12px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
						}}
					>
						{data && (
							<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
								<Typography component="div" color="primary" sx={{ fontSize: '0.9rem' }}>
									Client: <b>{data.client}</b>
								</Typography>
								<Typography component="div" color="primary" sx={{ fontSize: '0.9rem' }}>
									Invoice total (EUR):{' '}
									<b>
										{(
											data.total_invoice_amount_in_eur ??
											data.invoiced_amount_in_eur ??
											0
										).toLocaleString('en-US', { minimumFractionDigits: 2 })}
									</b>
								</Typography>
							</Box>
						)}
						<DatePickerComponent
							id="paymentDate"
							value={paymentDate}
							onChange={setPaymentDate}
							label="Payment date"
						/>
						<TextField
							id="paidAmount"
							name="paidAmount"
							size="small"
							label="Paid amount (EUR)"
							type="number"
							inputProps={{ min: 0, step: 0.01 }}
							value={formik.values.paidAmount}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							fullWidth
							required
							error={formik.touched.paidAmount && Boolean(formik.errors.paidAmount)}
							helperText={formik.touched.paidAmount && formik.errors.paidAmount}
						/>
						<FormControl
							fullWidth
							size="small"
							required
							error={formik.touched.paidThrough && Boolean(formik.errors.paidThrough)}
						>
							<InputLabel id="paidThrough-label">Paid through</InputLabel>
							<Select
								id="paidThrough"
								name="paidThrough"
								labelId="paidThrough-label"
								label="Paid through"
								value={formik.values.paidThrough}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
							>
								{PAID_THROUGH_OPTIONS.map((option: { value: string; label: string }) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
							{formik.touched.paidThrough && formik.errors.paidThrough && (
								<FormHelperText>{formik.errors.paidThrough}</FormHelperText>
							)}
						</FormControl>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							formik.resetForm();
							reset();
							setClose();
						}}
					>
						Cancel
					</Button>
					<Button
						type="submit"
						autoFocus
						disabled={Number(formik.values.paidAmount) < 0 || !formik.values.paidThrough}
					>
						Save
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
